import React from 'react';
import Swal from 'sweetalert2';
import moment from 'moment-timezone';
import churchForms from 'src/services/api/churchForms';
import {
  useRecaptchaToken,
  isCaptchaRequired,
  RECAPTCHA_SECURITY_ERROR,
} from 'src/components/helpers/recaptchaUtils';
import {
  hasFormErrors,
  verifyEditNetworkForm,
  withPendingRevision,
} from 'src/containers/comunidad-network/form/networkFormUtils';
import NetworkFormReviewNotice from 'src/containers/comunidad-network/form/NetworkFormReviewNotice';
import { NetworkFormEditOwner } from 'src/containers/comunidad-network/form/NetworkFormOwnerSection';
import { NetworkFormEditActions } from 'src/containers/comunidad-network/form/NetworkFormActions';
import NetworkFormLayout from 'src/containers/comunidad-network/form/NetworkFormLayout';
import NetworkCompanyFormBody from 'src/containers/comunidad-network/form/NetworkCompanyFormBody';
import {
  submitNetworkCompany,
  useNetworkCompanyForm,
} from 'src/containers/comunidad-network/form/useNetworkCompanyForm';

const EditComunidadEmpresa = ({ record, onSaved, onClose }) => {
  const getSubmitCaptchaToken = useRecaptchaToken('comunidad_network');
  const captchaRequired = isCaptchaRequired();
  const [guest, setGuest] = React.useState(record?.Guest || {});
  const {
    inputs,
    setInputs,
    errorInputs,
    setErrorInputs,
    sending,
    setSending,
    logoFile,
    logoPreview,
    catalogItems,
    handleInputs,
    selectHandle,
    setJobPostings,
    loadRecord,
    reset,
    media,
  } = useNetworkCompanyForm();

  const pendingRevision = record?.pendingRevision;

  React.useEffect(() => {
    if (!record) return;
    loadRecord(withPendingRevision(record));
    setGuest(record.Guest || {});
  }, [record, loadRecord]);

  const onGuestUpdated = (updated = {}) => {
    setGuest((prev) => ({ ...prev, ...updated }));
    setInputs((prev) => ({
      ...prev,
      identification: updated.identification ?? prev.identification,
      phone: updated.phone ?? prev.phone,
    }));
  };

  const finish = () => {
    reset();
    if (onSaved) onSaved();
    else onClose?.();
  };

  const discardRevision = async () => {
    const { value: confirmed } = await Swal.fire({
      title: 'Descartar cambios',
      text: 'El directorio seguirá mostrando la versión publicada actual.',
      showCancelButton: true,
      confirmButtonText: 'Descartar',
      cancelButtonText: 'Cancelar',
    });
    if (!confirmed) return;

    setSending(true);
    try {
      const response = await churchForms.postComunidadNetwork(
        { id: record.id, discardRevision: 'Si' },
        true
      );
      if (response?.data?.success) {
        Swal.fire('Cambios descartados');
        finish();
      } else {
        Swal.fire(response?.data?.message || 'Hubo un error, por favor intenta más tarde.');
      }
    } catch (error) {
      Swal.fire(error?.response?.data?.message || 'Hubo un error, por favor intenta más tarde.');
    } finally {
      setSending(false);
    }
  };

  const sendForm = async (e) => {
    e?.preventDefault?.();
    const nextErrors = verifyEditNetworkForm(inputs);
    setErrorInputs(nextErrors);
    if (hasFormErrors(nextErrors)) return;

    setSending(true);
    try {
      const { captchaFailed, response } = await submitNetworkCompany({
        captchaRequired,
        getCaptchaToken: getSubmitCaptchaToken,
        editing: true,
        formData: {
          inputs,
          logoFile,
          logoPreview,
          catalogItems,
          includePublished: true,
          extraFields: pendingRevision ? { clearPendingRevision: 'Si' } : {},
        },
      });
      if (captchaFailed) {
        Swal.fire(RECAPTCHA_SECURITY_ERROR);
        return;
      }

      if (response?.data?.success) {
        Swal.fire('Enviado correctamente');
        finish();
      } else if (response) {
        Swal.fire(response.data?.message || 'Hubo un error, por favor intenta más tarde.');
      }
    } catch (error) {
      Swal.fire(error?.response?.data?.message || 'Hubo un error, por favor intenta más tarde.');
    } finally {
      setSending(false);
    }
  };

  return (
    <NetworkFormLayout className="editing" title={`Editar: ${inputs.businessName || ''}`}>
      {pendingRevision && (
        <NetworkFormReviewNotice>
          Estás viendo los cambios que envió el propietario
          {pendingRevision.submittedAt
            ? ` el ${moment.tz(pendingRevision.submittedAt, 'America/Bogota').format('DD-MM-YYYY')}`
            : ''}
          . El directorio sigue mostrando la versión publicada hasta que guardes. Si descartas, la
          versión publicada se mantiene.
        </NetworkFormReviewNotice>
      )}
      <NetworkCompanyFormBody
        inputs={inputs}
        errorInputs={errorInputs}
        sending={sending}
        handleInputs={handleInputs}
        selectHandle={selectHandle}
        selectOnBlur={false}
        media={media}
        onJobPostingsChange={setJobPostings}
        owner={
          <NetworkFormEditOwner guest={guest} onGuestUpdated={onGuestUpdated} />
        }
        actions={
          <NetworkFormEditActions
            inputs={inputs}
            errorInputs={errorInputs}
            sending={sending}
            selectHandle={selectHandle}
            onClose={onClose}
            onDiscard={pendingRevision ? discardRevision : undefined}
            onSubmit={sendForm}
          />
        }
      />
    </NetworkFormLayout>
  );
};

export default EditComunidadEmpresa;
